import { useAtomValue } from "jotai";
import classNames from "classnames";
import { Node } from "@xyflow/react";
import { ServiceIcon } from "@zapier/design-system";
import { isViewOnlyModeAtom, Service } from "@zapier/canvas-state";

import useServiceById from "../../../hooks/useServiceById";
import cleanUpAppId from "../../../utils/cleanUpAppId";
import getAppIconUrl from "../../../utils/getAppIconUrl";
import { SidebarListItem } from "../SidebarListItem";

import styles from "./AppList.module.css";

const AppItemIcon = ({ item }: { item: Service }) => {
  const getService = useServiceById();

  const appId = item.currentImplementation.id;
  const service = getService(appId);
  const serviceName = service?.name ?? cleanUpAppId(appId);
  const images = service?.images;
  const src = getAppIconUrl(appId, images, 30);

  return (
    <span className={styles.serviceIcon} data-zds>
      <ServiceIcon
        alt={`${serviceName} icon`}
        hasBorder={false}
        size="small"
        hasShadow={false}
        serviceName={serviceName}
        src={src}
      />
    </span>
  );
};

export function AppList({
  services,
  node,
}: {
  services: Service[];
  node: Node;
}) {
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const appId = services?.[0]?.currentImplementation.id ?? "";
  const getService = useServiceById();
  const service = getService(appId);

  const initialSelectedItem =
    service ??
    (node.data.app
      ? {
          name: node.data.app as string,
          app_id: appId,
        }
      : undefined);

  if (!initialSelectedItem || !services?.[0]) {
    return null;
  }

  const listStyles = classNames(styles.appList, {
    [styles.row]: isViewOnlyMode,
  });

  return (
    <ul className={listStyles}>
      <SidebarListItem
        icon={<AppItemIcon item={services?.[0]} />}
        label={initialSelectedItem.name}
        key={initialSelectedItem.app_id}
        isViewOnly={isViewOnlyMode}
      />
    </ul>
  );
}
